import { useMutation } from '@tanstack/react-query';
import { pushToast } from '../../lib/events';
import { removeMember, setMemberRole, type Member, type RoomRole } from '../../lib/rooms';

interface HostRailMembersProps {
  roomId: number;
  members: Member[];
  myRole: RoomRole;
}

const ROLE_OPTIONS: RoomRole[] = ['HOST', 'COHOST', 'GUEST', 'VIEWER'];

/** Role select + remove, host-only; everyone else sees a plain role chip. No invalidate-and-refetch
 * on success: a member-role/removal mutation triggers a `room` STOMP broadcast (see
 * `useRoomRealtime`), which carries the fresh member list straight into the store. */
export function HostRailMembers({ roomId, members, myRole }: HostRailMembersProps) {
  const isHost = myRole === 'HOST';

  const roleMutation = useMutation({
    mutationFn: setMemberRole,
    onError: () => pushToast('Could not change that member’s role.'),
  });
  const removeMutation = useMutation({
    mutationFn: removeMember,
    onError: () => pushToast('Could not remove that member.'),
  });

  return (
    <section>
      <div className="flex items-baseline gap-4">
        <span className="eyebrow">Members</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
      <ul className="mt-3 flex flex-col gap-1.5">
        {members.map((member) => (
          <li key={member.userId} className="flex items-center justify-between gap-2 rounded-md border border-border-strong px-3 py-2">
            <span className="truncate text-sm font-semibold text-text">{member.username}</span>
            {isHost ? (
              <div className="flex shrink-0 items-center gap-1">
                <select
                  value={member.role}
                  disabled={roleMutation.isPending}
                  onChange={(event) =>
                    roleMutation.mutate({ roomId, userId: member.userId, role: event.target.value as RoomRole })
                  }
                  aria-label={`${member.username}'s role`}
                  className="min-h-tap rounded-md border border-border-strong bg-bg px-1.5 text-xs text-text"
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={removeMutation.isPending}
                  onClick={() => removeMutation.mutate({ roomId, userId: member.userId })}
                  className="min-h-tap rounded-md border border-border-strong px-2 text-xs font-semibold text-text-muted hover:border-signal hover:text-signal disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ) : (
              <span className="eyebrow shrink-0">{member.role}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
